import React, { useState, useMemo } from "react";
import "./NewPassword.scss";
import BackButton from "../../global/BackButton/BackButton";
import Button from "../../global/form/Button/Button";
import { useDispatch } from "react-redux";
import { onEnterPressed } from "../../../helper/eventHelper";
import { updatePasswordWithCode } from "../../../redux/actions/recoveryPassword";
import ConfirmPopUp from "../../global/ConfirmPopUp/ConfirmPopUp";
import { useHistory, useParams } from "react-router";

// ── password strength helpers ──────────────────────────────────────────────
const getStrength = (pwd) => {
  if (!pwd) return null;
  const hasLetter  = /[a-zA-Z]/.test(pwd);
  const hasNumber  = /[0-9]/.test(pwd);
  const hasSpecial = /[^a-zA-Z0-9]/.test(pwd);
  const long       = pwd.length >= 8;
  const types      = [hasLetter, hasNumber, hasSpecial].filter(Boolean).length;

  if (pwd.length < 6 || types < 2)      return "fraca";
  if (long && types === 3)               return "forte";
  return "média";
};

const STRENGTH_META = {
  fraca:  { label: "Fraca",  bars: 1, color: "#f87171" },
  média:  { label: "Média",  bars: 2, color: "#f3a543" },
  forte:  { label: "Forte",  bars: 3, color: "#4ade80" },
};

const NewPassword = () => {
  const { code }    = useParams();
  const dispatch    = useDispatch();
  const history     = useHistory();

  const [password,             setPassword]             = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [showPassword,         setShowPassword]         = useState(false);
  const [showConfirm,          setShowConfirm]          = useState(false);
  const [submitError,          setSubmitError]          = useState("");
  const [openPopUp,            setOpenPopUp]            = useState(false);
  const [isLoading,            setIsLoading]            = useState(false);
  const [validLink,            setValidLink]            = useState(true);

  const strength      = useMemo(() => getStrength(password), [password]);
  const meta          = strength ? STRENGTH_META[strength] : null;
  const passwordsMatch =
    passwordConfirmation.length > 0 && password === passwordConfirmation;
  const passwordsMismatch =
    passwordConfirmation.length > 0 && password !== passwordConfirmation;

  const isValidPassword = (pwd) =>
    pwd.length >= 6 &&
    /[a-zA-Z]/.test(pwd) &&
    /[0-9]/.test(pwd) &&
    /[^a-zA-Z0-9]/.test(pwd);

  const handleSubmit = () => {
    setSubmitError("");

    if (!isValidPassword(password)) {
      setSubmitError(
        "A senha deve ter ao menos 6 caracteres, incluindo letras, números e um caractere especial."
      );
      return;
    }
    if (password !== passwordConfirmation) {
      setSubmitError("As senhas informadas não são iguais.");
      return;
    }

    setIsLoading(true);
    dispatch(updatePasswordWithCode(code, { password }))
      .then(() => {
        setOpenPopUp(true);
        setIsLoading(false);
        localStorage.clear();
      })
      .catch(() => {
        setValidLink(false);
        setIsLoading(false);
        setSubmitError(
          "O link de redefinição de senha expirou. Solicite um novo link."
        );
      });
  };

  return (
    <section className="NewPassword">
      <BackButton />
      <h2>Nova senha</h2>
      <p className="np-description">Escolha uma senha segura para a sua conta.</p>

      {submitError && <p className="np-error">{submitError}</p>}

      <form onSubmit={(e) => e.preventDefault()}>
        {/* ── Nova senha ── */}
        <div className="np-field">
          <label className="np-label">NOVA SENHA</label>
          <div className="np-input-wrapper">
            <input
              className="np-input"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyUp={(e) => onEnterPressed(e, handleSubmit)}
              placeholder="••••••••"
              autoComplete="new-password"
            />
            <button
              type="button"
              className="np-eye"
              onClick={() => setShowPassword((v) => !v)}
              tabIndex={-1}
              aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
            >
              {showPassword ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              )}
            </button>
          </div>

          {/* ── Força da senha ── */}
          {strength && (
            <div className="np-strength">
              <div className="np-strength-bars">
                {[1, 2, 3].map((n) => (
                  <div
                    key={n}
                    className="np-strength-bar"
                    style={{
                      background:
                        n <= meta.bars ? meta.color : "rgba(255,255,255,0.12)",
                    }}
                  />
                ))}
              </div>
              <span className="np-strength-label" style={{ color: meta.color }}>
                {meta.label}
              </span>
            </div>
          )}

          <p className="np-hint">
            Mínimo 6 caracteres com letras, números e 1 caractere especial.
          </p>
        </div>

        {/* ── Confirmação ── */}
        <div className="np-field">
          <label className="np-label">CONFIRMAÇÃO DE SENHA</label>
          <div
            className={`np-input-wrapper${
              passwordsMatch   ? " np-input-wrapper--ok"    :
              passwordsMismatch ? " np-input-wrapper--error" : ""
            }`}
          >
            <input
              className="np-input"
              type={showConfirm ? "text" : "password"}
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              onKeyUp={(e) => onEnterPressed(e, handleSubmit)}
              placeholder="••••••••"
              autoComplete="new-password"
            />
            <button
              type="button"
              className="np-eye"
              onClick={() => setShowConfirm((v) => !v)}
              tabIndex={-1}
              aria-label={showConfirm ? "Ocultar senha" : "Mostrar senha"}
            >
              {showConfirm ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              )}
            </button>

            {/* ícone de status match */}
            {passwordsMatch && (
              <svg className="np-match-icon np-match-icon--ok" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            )}
            {passwordsMismatch && (
              <svg className="np-match-icon np-match-icon--error" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            )}
          </div>

          {passwordsMismatch && (
            <p className="np-field-error">As senhas não são iguais.</p>
          )}
          {passwordsMatch && (
            <p className="np-field-ok">Senhas conferem!</p>
          )}
        </div>
      </form>

      {validLink ? (
        <Button bottom type="button" onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? "Salvando..." : "Alterar senha"}
        </Button>
      ) : (
        <Button
          bottom
          className="secondary"
          type="button"
          onClick={() => history.push("/recuperar-senha")}
        >
          Reenviar link
        </Button>
      )}

      <ConfirmPopUp
        message="Nova senha cadastrada com sucesso!"
        active={openPopUp}
        buttonAction={() => history.push("/login")}
        buttonText="Fazer login"
      />
    </section>
  );
};

export default NewPassword;
